<template>
  <v-container fluid>
    <PageHeaderSlot>
      <template slot="top-right-slot" v-if="isTutor">
        <FormButton text="applyLeave" class="ml-2" isAdd @click="handleApply()" />
      </template>
    </PageHeaderSlot>

    <Datatable
      :disabledSelectAll="filter.status !== 'pending'"
      :isLoading="tableLoading"
      :tableData="tableData"
      :tableHeaders="tableHeaders"
      @options-update="getApplications()"
      :page.sync="filter.page"
      :pageLimit.sync="filter.limit"
      :itemTotal="tableItemTotal"
      noWrapToolbar
    >
      <template slot="append-toolbar-right">
        <div style="width: 250px;">
          <FormSelect
            :fieldValue.sync="filter.status"
            :options="statusOptions"
            hideDetails
            placeholder="searchStatus"
            class="my-2"
            @changed="searchStatus()"
            enableClear
            :readonly="tableLoading"
          />
        </div>
      </template>
      <template v-slot:[`item.actionStatus`]="{ item }">
        <div class="d-flex" v-if="item.status === 'pending' && canAudit">
          <v-btn
            type="button"
            depressed
            text
            color="success"
            :loading="$store.getters.isLoading"
            @click.prevent="handleStatusActionClick('accepted', item.id)"
          >
            {{ $t('accept') }}
          </v-btn>
          <v-btn
            type="button"
            depressed
            text
            color="red"
            :loading="$store.getters.isLoading"
            @click.prevent="handleStatusActionClick('rejected', item.id)"
          >
            {{ $t('reject') }}
          </v-btn>
        </div>
        <v-chip
          v-else
          :class="{
            'chip-success': item.status === 'accepted',
            'chip-error': item.status === 'rejected',
            'chip-inactive': item.status === 'pending',
          }"
        >
          {{ $t(`requirementStatus.${item.status}`) }}
        </v-chip>
      </template>
    </Datatable>

    <!-- Modal: 申請請假 -->
    <FormDialog ref="applyLeaveDialog" formRef="applyLeaveForm" title="applyLeave" :maxWidth="500">
      <v-row class="ma-0">
        <v-col cols="12">
          <FormDatePicker label="date" :dateValue.sync="leaveDate" disabledActionButtons required />
        </v-col>
        <v-col cols="12">
          <FormInput label="remark" :fieldValue.sync="leaveRemark" />
        </v-col>
      </v-row>
    </FormDialog>
  </v-container>
</template>

<script>
import Datatable from '@/components/Datatable.vue'
import FormSelect from '@/components/formField/FormSelect.vue'
import FormDatePicker from '@/components/formField/FormDatePicker.vue'
import FormDialog from '@/components/FormDialog.vue'
import FormInput from '@/components/formField/FormInput.vue'

export default {
  name: 'TutorLeave',
  inject: ['getConfirmDialogRef'],
  components: {
    Datatable,
    FormSelect,
    FormDatePicker,
    FormDialog,
    FormInput,
  },
  computed: {
    confirmRef() {
      return this.getConfirmDialogRef()
    },
    isTutor() {
      return this.userType === 'tutor'
    },
    tableHeaders() {
      const vm = this
      return [
        { value: 'create_date', text: vm.$t('createdDate'), width: 130 },
        { value: 'name', text: vm.$t('name'), width: 200 },
        { value: 'date', text: vm.$t('leaveDate'), width: 130 },
        { value: 'comment', text: vm.$t('remark') },
        {
          value: 'actionStatus',
          text: !vm.canAudit || vm.filter.status !== 'pending' ? vm.$t('status') : '',
          align: 'center',
          width: !vm.canAudit || vm.filter.status !== 'pending' ? 150 : 250,
        },
      ]
    },
    // permission & setting
    canAudit() {
      return this.$validate.allowAction('canAuditLeave', this.userType)
    },
  },
  data: vm => ({
    userType: vm.gfGetUserType(),

    tableLoading: false,
    tableData: [],
    tableItemTotal: 0,
    filter: {
      page: 1,
      limit: 10,
      status: '',
    },

    leaveDate: '',
    leaveRemark: '',

    // - static
    statusOptions: [
      { text: vm.$t('requirementStatus.pending'), value: 'pending' },
      { text: vm.$t('requirementStatus.accepted'), value: 'accepted' },
      { text: vm.$t('requirementStatus.rejected'), value: 'rejected' },
    ],
  }),
  methods: {
    async getApplications(regenUrl = true) {
      if (regenUrl === true) {
        this._regenerateURL()
      }
      this.tableLoading = true
      try {
        let payload = {
          filter_limit: this.filter.limit,
          filter_page: this.filter.page - 1,
          join_tutor_data: true,
          filter_item: [],
        }

        if (!this.canAudit) {
          const getUser = this.gfGetCurrentUserData()
          payload['filter_item'].push({ key: 'staff_id', value: getUser.id })
        }
        if (this.filter.status) {
          payload['filter_item'].push({ key: 'status', value: this.filter.status })
        }

        const { data, total } = await this.$Fetcher.GetLeaveApplications(payload)
        this.tableData = data

        this.tableItemTotal = total
      } catch (err) {
        this.$common.error(err)
        this.tableData = []
        if (this.filter.page === 1) {
          this.tableItemTotal = 0
        }
      } finally {
        this.tableLoading = false
      }
    },
    searchStatus() {
      this.filter.page = 1
      this.tableSelectedRow = []
      this.getApplications()
    },

    _getQuery() {
      this.filter.page = 1
      this.filter.limit = 10
      this.filter.status = ''
      if (this.userType !== 'tutor') {
        this.filter.status = 'pending'
      }
      const query = this.gfGetTableQuery({
        page: 'int',
        limit: 'int',
        status: ['pending', 'accepted', 'rejected'],
      })
      for (const key in this.filter) {
        if (this.$validate.DataValid(query[key])) {
          this.$set(this.filter, key, query[key])
        }
      }
    },
    _regenerateURL() {
      let q = {
        page: this.filter.limit !== 10 || this.filter.page !== 1 ? this.filter.page : null,
        limit: this.filter.limit !== 10 || this.filter.page !== 1 ? this.filter.limit : null,
        status: this.filter.status,
      }
      const newQuery = this.$common.diffQuery(q, this.$route.query)
      if (newQuery) {
        this.$router.replace({ query: newQuery })
      }
    },

    // -- (接受/拒絕)
    async handleStatusActionClick(status, id) {
      if (!this.canAudit) return
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      const found = this.tableData.find(el => el.id === id)
      if (found.status !== 'pending') {
        this.showAlert({
          title: 'message.updateFail',
          message: 'message.notPendingApplyExist',
          isError: true,
        })
        return
      }

      const confirm = await this.confirmRef.show({
        title: 'message.confirmUpdateApplyStatus',
        message: 'message.confirmUpdateApplyStatusMsg',
      })
      if (confirm) {
        this.$store.dispatch('setLoading', true)

        try {
          if (status === 'accepted') {
            await this.$Fetcher.ApproveLeaveApplication(id)
          } else if (status === 'rejected') {
            await this.$Fetcher.RejectLeaveApplication(id)
          }

          this.$store.dispatch('toggleSnack', {
            message: 'message.updateSuccess',
            type: 'success',
          })

          this.$store.dispatch('setLoading', false)
          this.filter.page = 1
          this.getApplications()
        } catch (err) {
          this.$store.dispatch('setLoading', false)
          this.showAlert({
            title: 'message.updateFail',
            message: err,
            isError: true,
          })
        }
      }
    },

    // -- (申請請假)
    async handleApply(newOpen = true) {
      // reset form when open
      if (newOpen) {
        if (this.$store.getters.isLoading) {
          this.$store.dispatch('toggleSnack', { message: 'processing' })
          return
        }
        this.leaveDate = ''
        this.leaveRemark = ''
        await this.$refs.applyLeaveDialog.resetForm()
      } else {
        this.$refs.applyLeaveDialog.setLoading(false)
      }

      // handle form submit
      const confirm = await this.$refs.applyLeaveDialog.show()
      if (confirm) {
        try {
          const getUser = this.gfGetCurrentUserData()
          await this.$Fetcher.ApplyLeave(
            {
              date: this.leaveDate,
              comment: this.leaveRemark,
            },
            getUser.id,
          )
          this.$store.dispatch('toggleSnack', {
            message: 'message.submitSuccess',
            type: 'success',
          })
          this.$refs.applyLeaveDialog.hide()
          this.tableLoading = true
          setTimeout(() => {
            this.filter.page = 1
            this.getApplications()
          }, 500)
        } catch (err) {
          this.showAlert({
            title: 'message.submitFail',
            message: err,
            isError: true,
          })
          this.handleApply(false)
        }
      }
    },

    async handleSiteLoaded() {
      this.$common.log('>>>>>>>>>>>>>>>>>>>>>>>>>>>> handleSiteLoaded: TutorLeave')
      this.userType = this.gfGetUserType()
      this._getQuery()
      this.$store.dispatch('toggleLoadingPage', false)

      await this.getApplications(false)
    },
  },
  destroyed() {
    window.removeEventListener('onSiteLoaded', this.handleSiteLoaded)
  },
  mounted() {
    this.tableLoading = true
    this.$store.dispatch('toggleLoadingPage', true)
    window.addEventListener('onSiteLoaded', this.handleSiteLoaded)
  },

  // ------ navigation guard ------
  beforeRouteLeave(to, from, next) {
    if (this.$store.getters.isLoading) {
      this.$store.dispatch('toggleSnack', { message: 'processing' })
      next(false)
    } else {
      this.$XHR.abort()
      next()
    }
  },
}
</script>
